import {ChangeEvent, FC, KeyboardEvent, useLayoutEffect, useRef, useState} from 'react';
import {extractAccountDigits, formatAccountDisplay, getAccountNumberError, RegistrationWorkStatus} from '../../model';
import {StepLayout} from 'features/registration';
import {TextInput} from '../components/TextInput';
import styles from './PaymentDetailsStep.module.css';

type Props = {
	value: string;
	workStatus: RegistrationWorkStatus | null;
	error?: string | null;
	onChange: (accountDigits: string) => void;
	onClearError?: () => void;
};

const caretPosAfterDigits = (display: string, digitCount: number): number => {
	if (digitCount <= 0) {
		return 0;
	}

	let seen = 0;

	for (let i = 0; i < display.length; i++) {
		if (/\d/.test(display[i]!)) {
			seen += 1;

			if (seen === digitCount) {
				return i + 1;
			}
		}
	}

	return display.length;
};

export const PaymentAccountNumberStep: FC<Props> = ({value, workStatus, error, onChange, onClearError}) => {
	const accountDigits = extractAccountDigits(value);
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
	const [caretDigits, setCaretDigits] = useState<number | null>(null);

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		const input = event.target;
		const nextRaw = input.value;
		const nextAccount = extractAccountDigits(nextRaw);
		const caret = input.selectionStart ?? nextRaw.length;
		const digitsBeforeCaret = extractAccountDigits(nextRaw.slice(0, caret)).length;

		onClearError?.();
		onChange(nextAccount);
		setCaretDigits(digitsBeforeCaret);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key !== 'Backspace') {
			return;
		}

		const input = event.currentTarget;
		const caret = input.selectionStart ?? 0;
		const selectionEnd = input.selectionEnd ?? 0;

		if (caret !== selectionEnd || caret === 0) {
			return;
		}

		const display = formatAccountDisplay(accountDigits);
		const prevChar = display[caret - 1];

		if (prevChar !== ' ') {
			return;
		}

		event.preventDefault();

		const digitsBeforeCaret = extractAccountDigits(display.slice(0, caret)).length;

		if (digitsBeforeCaret === 0) {
			return;
		}

		const nextDigits = accountDigits.slice(0, digitsBeforeCaret - 1) + accountDigits.slice(digitsBeforeCaret);

		onClearError?.();
		onChange(nextDigits);
		setCaretDigits(digitsBeforeCaret - 1);
	};

	useLayoutEffect(() => {
		if (caretDigits === null || !inputRef.current) {
			return;
		}

		const pos = caretPosAfterDigits(formatAccountDisplay(accountDigits), caretDigits);
		inputRef.current.setSelectionRange(pos, pos);
		setCaretDigits(null);
	}, [accountDigits, caretDigits]);

	const handleClear = () => {
		onClearError?.();
		onChange('');
	};

	const displayError = error ?? getAccountNumberError(value, {workStatus});

	return (
		<StepLayout variant="fill">
			<StepLayout.Title>Введите номер счёта</StepLayout.Title>

			<StepLayout.Content className={styles.field}>
				<TextInput
					ref={inputRef}
					name="paymentAccount"
					className={styles.input}
					value={formatAccountDisplay(value)}
					placeholder="40802 810 40 58316 76842"
					inputMode="numeric"
					autoComplete="off"
					autoFocus
					aria-invalid={Boolean(displayError)}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					onClear={handleClear}
				/>
				{displayError ? <p className={styles.error}>{displayError}</p> : null}
			</StepLayout.Content>
		</StepLayout>
	);
};
